import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Users, Star, BookOpen, GraduationCap, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Teachers() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [teachers, setTeachers] = useState(null);
  const load = () => api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => setTeachers([]));
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`حذف المحاضر "${t.name}" نهائيًا؟ سيتم فك ارتباطه بالبرامج والمحاضرات.`)) return;
    try { await api.delete(`/users/${t.id}`); toast.success("تم حذف المحاضر"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (teachers === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="المدرسون والمحاضرون" subtitle="بيانات المدرسين وتقييماتهم" />
      {teachers.length === 0 ? <EmptyState icon={Users} title="لا يوجد مدرسون" desc="سيظهر هنا المدرسون بعد تسجيلهم." /> : (
        <div className="stagger grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {teachers.map((t) => (
            <div key={t.id} data-testid={`teacher-${t.id}`} className="card-hover group relative rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
              {isAdmin && (
                <Button onClick={() => remove(t)} data-testid={`del-teacher-${t.id}`} size="sm" variant="ghost"
                  className="absolute left-3 top-3 h-8 w-8 p-0 text-red-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-lg font-bold text-gold">{t.name?.charAt(0)}</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-navy">{t.name}</h3>
                  <p className="text-sm text-slate-400">{t.specialization || "—"}</p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <Metric icon={BookOpen} value={t.programs_count} label="برامج" />
                <Metric icon={GraduationCap} value={t.lectures_count} label="محاضرات" />
                <Metric icon={Clock} value={t.teaching_hours} label="ساعة" />
              </div>
              <div className="mt-4 flex items-center justify-between rounded-xl bg-amber-50 px-4 py-2">
                <span className="text-sm font-semibold text-gold-dark">تقييم الطلاب</span>
                <span className="flex items-center gap-1 font-bold text-gold-dark"><Star className="h-4 w-4 fill-gold text-gold" /> {t.rating || "—"} <span className="text-xs text-slate-400">({t.rating_count})</span></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Metric({ icon: Icon, value, label }) {
  return (
    <div className="rounded-xl bg-slate-50 py-3">
      <Icon className="mx-auto h-4 w-4 text-slate-400" />
      <p className="mt-1 font-heading text-lg font-bold text-navy">{value}</p>
      <p className="text-[11px] text-slate-400">{label}</p>
    </div>
  );
}
